import React from "react";
import { reportFrontendError } from "@/lib/fe_errors";

/**
 * App-wide ErrorBoundary.
 *
 * Wraps <App /> so a render error anywhere in the tree degrades to a
 * friendly fallback instead of the dreaded blank white screen that
 * Facebook in-app browser users were hitting.
 *
 * Every caught error is forwarded to the frontend error reporter so we
 * can see what broke in the admin audit log.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || "Something went wrong." };
  }

  componentDidCatch(error, info) {
    reportFrontendError({
      kind: "react.errorboundary",
      message: (error && error.message) || "",
      stack: (error && error.stack) || "",
      componentStack: info && info.componentStack,
    });
  }

  _reload = () => {
    try {
      // Full reload — the component tree is already poisoned.
      window.location.reload();
    } catch {
      // no-op
    }
  };

  _openInSystemBrowser = () => {
    try {
      const url = window.location.href;
      // Opening in a new window from inside the FB in-app browser prompts
      // iOS/Android to hand off to Safari / Chrome. Not guaranteed on all
      // devices but materially better than being stuck.
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // no-op
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        data-testid="error-boundary-fallback"
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f8fafc",
          color: "#0f172a",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 440,
            width: "100%",
            background: "#ffffff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
            padding: 24,
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
            Something didn&apos;t load right.
          </div>
          <div style={{ fontSize: 14, color: "#475569", marginBottom: 16, lineHeight: 1.5 }}>
            This can happen inside the Facebook or Instagram app browser on
            older phones. Try reloading, or open this page in Safari / Chrome
            for the full experience.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              onClick={this._reload}
              data-testid="error-boundary-reload"
              style={{
                height: 48,
                borderRadius: 10,
                background: "#0284c7",
                color: "white",
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                fontSize: 15,
              }}
            >
              Reload page
            </button>
            <button
              onClick={this._openInSystemBrowser}
              data-testid="error-boundary-open-system"
              style={{
                height: 48,
                borderRadius: 10,
                background: "white",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 15,
              }}
            >
              Open in Safari / Chrome
            </button>
          </div>
          {this.state.message ? (
            <div
              style={{
                marginTop: 16,
                fontSize: 11,
                color: "#94a3b8",
                wordBreak: "break-word",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
              data-testid="error-boundary-detail"
            >
              {this.state.message}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}
